import { useRef, useEffect, useCallback, useMemo } from 'preact/hooks';
import type { JSX } from 'preact';
import { renderMarkdown } from '../utils/renderMarkdown';
import { PresentationCard } from './PresentationCard';
import { getHljs } from '../utils/highlightLoader';
import { resolveMarkdownHref } from '../utils/resolveMarkdownHref';
import { useDocTtsStore } from '../state/docTtsStore';

export interface TtsDocSegment {
  index: number;
  ttsText: string;
  skip: boolean;
}

interface Props {
  text: string;
  class?: string;
  /** When true, skip expensive post-processing (linkify, media embed, mermaid, syntax highlight) */
  streaming?: boolean;
  /**
   * Directory of the markdown document being rendered. Used to resolve relative
   * hrefs in markdown links (`[foo](bar.md)`) into workspace paths so clicking
   * them dispatches `open-file-viewer` instead of triggering browser navigation.
   */
  basePath?: string;
  /** TTS reading mode — annotates block elements with data-tts-segment="N" */
  ttsMode?: boolean;
  /** Index of the currently-playing segment (gets .tts-active class) */
  activeTtsSegment?: number;
  /** Called when user clicks a paragraph to seek to that segment */
  onTtsSegmentClick?: (segmentIndex: number) => void;
  /** Called once after DOM annotation is complete with the full segment list */
  onSegmentsReady?: (segments: TtsDocSegment[]) => void;
  /**
   * Architecture-map opt-in (plans/architecture-map-c4.md). When set, a click on a mermaid node
   * whose `xlink:href` starts with `#arch/` calls this with that href INSTEAD of opening the
   * diagram image lightbox. When unset (all chat usage), mermaid clicks lightbox as before.
   */
  onDiagramNodeClick?: (target: string) => void;
}

/** Extract ```presentation fenced blocks from text, return remaining text + parsed presentations. */
const PRES_FENCE_RE = /```presentation\s*\n([\s\S]*?)```/g;
function extractPresentations(text: string): { cleanText: string; presentations: Array<{ title: string; slides: string[] }> } {
  const presentations: Array<{ title: string; slides: string[] }> = [];
  const cleanText = text.replace(PRES_FENCE_RE, (_, content: string) => {
    const rawSlides = content.split(/\n---\n/);
    const slides = rawSlides.map((s) => renderMarkdown(s.trim()));
    // Use first H1/H2 as title, or fallback
    const titleMatch = content.match(/^#+\s+(.+)/m);
    const title = titleMatch ? titleMatch[1] : 'Presentation';
    presentations.push({ title, slides });
    return ''; // Remove from markdown text
  });
  return { cleanText, presentations };
}

/* highlight.js: shared loader in utils/highlightLoader.ts */

let mermaidReady: Promise<typeof import('mermaid')> | null = null;
let mermaidInitKey: string | null = null;

/** Mutex: serializes mermaid.render() calls to avoid concurrent render corruption in mermaid 11.x */
let renderQueue: Promise<void> = Promise.resolve();

type TV = Record<string, string | boolean>;

/**
 * Three diagram presets — WCAG AA verified (Gemini + Opus, 2026-04-09).
 *
 * Key fix for all presets: signalColor is used by Mermaid 11.x as BOTH the arrow line stroke
 * AND the signal label background. Always pair signalColor with an explicit contrasting
 * signalTextColor — never rely on the textColor fallback.
 *
 * All presets use theme:'base' (not 'neutral' for light) for predictable variable inheritance.
 */

/** Minimal — clean/light, high readability. Default for zen (r) + slate (t/k) app themes. */
const PRESET_MINIMAL: TV = {
  darkMode: false, background: '#ffffff', mainBkg: '#ffffff',
  primaryColor: '#f0f0f0', primaryTextColor: '#1a1a1a', primaryBorderColor: '#dcdcdc',
  lineColor: '#666666', secondaryColor: '#e8e8f8', tertiaryColor: '#e8f4ee',
  textColor: '#1a1a1a',
  clusterBkg: '#f8f8f8', titleColor: '#1a1a1a', edgeLabelBackground: '#f0f0f0',
  noteBkgColor: '#f8f8f8', noteTextColor: '#1a1a1a',
  // Sequence diagram — signalColor as arrow accent, signalTextColor must contrast against it
  actorBkg: '#f0f0f0', actorTextColor: '#1a1a1a', actorBorder: '#9090c0',  // 10.4:1
  actorLineColor: '#666666',
  signalColor: '#007BFF', signalTextColor: '#ffffff',                       // 4.54:1 on blue
  labelBoxBkgColor: '#fffbe0', labelTextColor: '#1a1a1a', loopTextColor: '#1a1a1a', // 12.0:1
  activationBorderColor: '#9090c0', activationBkgColor: '#e8e8f8',
  sequenceNumberColor: '#ffffff',
  // Timeline / gantt — light enough to read dark text on top
  cScale0: '#dde8ff', cScale1: '#d8eef8', cScale2: '#d8f0e8', cScale3: '#f0dde8',
  cScale4: '#e8f0d8', cScale5: '#f0e8d8', cScale6: '#d8e4f0', cScale7: '#ead8ea',
};

/** Rich — deep dark, saturated. Default for moss (B/V) + indigo (null) app themes. */
const PRESET_RICH: TV = {
  darkMode: true, background: '#1a2020', mainBkg: '#1a2020',
  primaryColor: '#2c3e50', primaryTextColor: '#ecf0f1', primaryBorderColor: '#4b6584',
  lineColor: '#bdc3c7', secondaryColor: '#1a3040', tertiaryColor: '#1a3028',
  textColor: '#ecf0f1',
  clusterBkg: '#223030', titleColor: '#ecf0f1', edgeLabelBackground: '#1e2828',
  noteBkgColor: '#2a3010', noteTextColor: '#e8d070',
  // Sequence diagram
  actorBkg: '#34495e', actorTextColor: '#ecf0f1', actorBorder: '#5d7a9e',  // 7.15:1
  actorLineColor: '#bdc3c7',
  signalColor: '#27ae60', signalTextColor: '#ffffff',                       // 4.58:1 on emerald
  labelBoxBkgColor: '#3a4a5a', labelTextColor: '#ecf0f1', loopTextColor: '#ecf0f1', // 6.27:1
  activationBorderColor: '#5d7a9e', activationBkgColor: '#34495e',
  sequenceNumberColor: '#ecf0f1',
  // Timeline / gantt — mid-tone so both light text and the section label are readable
  cScale0: '#2a5040', cScale1: '#20405a', cScale2: '#303850', cScale3: '#402840',
  cScale4: '#1e4848', cScale5: '#3a3028', cScale6: '#204830', cScale7: '#382038',
};

/** Vibrant — bold/candy, high visual impact. Default for amber (a) app theme. */
const PRESET_VIBRANT: TV = {
  darkMode: false, background: '#e8f4ff', mainBkg: '#e8f4ff',
  primaryColor: '#b3e5fc', primaryTextColor: '#1e3a8a', primaryBorderColor: '#81d4fa',
  lineColor: '#0288d1', secondaryColor: '#e0f2e8', tertiaryColor: '#fff8e0',
  textColor: '#1e3a8a',
  clusterBkg: '#e0f2f7', titleColor: '#1e3a8a', edgeLabelBackground: '#e8f4ff',
  noteBkgColor: '#ffecb3', noteTextColor: '#1a1a1a',
  // Sequence diagram
  actorBkg: '#b3e5fc', actorTextColor: '#1e3a8a', actorBorder: '#4fc3f7',  // 4.54:1
  actorLineColor: '#0288d1',
  signalColor: '#d32f2f', signalTextColor: '#ffffff',                       // 4.51:1 on red
  labelBoxBkgColor: '#ffd700', labelTextColor: '#1a1a1a', loopTextColor: '#1a1a1a', // 10.4:1
  activationBorderColor: '#4fc3f7', activationBkgColor: '#e0f4ff',
  sequenceNumberColor: '#1e3a8a',
  // Timeline / gantt — bright, saturated, with dark text always readable
  cScale0: '#b3e5fc', cScale1: '#c8f0d0', cScale2: '#fff9c4', cScale3: '#ffccbc',
  cScale4: '#e1bee7', cScale5: '#f8bbd0', cScale6: '#b2dfdb', cScale7: '#ffe0b2',
};

/** Map app theme code → preset name (used when diagramStyle === 'auto'). */
function autoPreset(appTheme: string | null): TV {
  if (appTheme === 'r' || appTheme === 't' || appTheme === 'k') return PRESET_MINIMAL;
  if (appTheme === 'a') return PRESET_VIBRANT;
  return PRESET_RICH; // B, V, null (indigo default)
}

function getMermaid() {
  const appTheme = document.documentElement.getAttribute('data-theme') || null;
  const diagramStyle = document.documentElement.getAttribute('data-diagram-style') || 'auto';
  const initKey = `${appTheme}:${diagramStyle}`;

  // Re-initialize if app theme or diagram style changed
  if (mermaidReady && mermaidInitKey !== initKey) {
    mermaidReady = null;
  }
  if (!mermaidReady) {
    mermaidInitKey = initKey;
    mermaidReady = import('mermaid').then((m) => {
      let themeVariables: TV;
      if (diagramStyle === 'minimal') themeVariables = PRESET_MINIMAL;
      else if (diagramStyle === 'rich') themeVariables = PRESET_RICH;
      else if (diagramStyle === 'vibrant') themeVariables = PRESET_VIBRANT;
      else themeVariables = autoPreset(appTheme); // 'auto'

      m.default.initialize({
        startOnLoad: false,
        theme: 'base', // always 'base' — more predictable variable inheritance than 'neutral'
        themeVariables,
        // Loose security allows click handlers, htmlLabels, etc — needed for emoji in labels
        securityLevel: 'loose',
      });
      return m;
    }).catch((err) => {
      // Reset so next call retries the import instead of returning a permanently rejected promise
      console.error('[mermaid] Failed to load/initialize:', err);
      mermaidReady = null;
      mermaidInitKey = null;
      throw err;
    });
  }
  return mermaidReady;
}

/** Enqueue a mermaid render so only one runs at a time (mermaid 11.x shared state). */
function enqueueMermaidRender<T>(fn: () => Promise<T>): Promise<T> {
  const task = renderQueue.then(fn, fn); // run even if previous rejected
  renderQueue = task.then(() => {}, () => {}); // swallow to keep chain alive
  return task;
}

/** Remove stray mermaid render containers from the DOM body.
 *  Mermaid 11.x creates `<div id="d{renderId}">` on `document.body` during `render()`.
 *  On success it returns the SVG but may leave the container behind.
 *  On failure it leaves the container with an error message inside. */
function cleanupMermaidErrors(renderId?: string) {
  // Remove the specific render container if we know the id
  if (renderId) {
    document.getElementById(`d${renderId}`)?.remove();
  }
  // Broad sweep: any body-level divs whose id starts with "dmermaid-"
  document.querySelectorAll('body > div[id^="dmermaid-"]').forEach((el) => el.remove());
}

/** Strip outer code fence if the entire text is wrapped in one (Claude sometimes does this).
 *  IMPORTANT: preserve fences with language tags (```mermaid, ```json, etc.)
 *  — marked needs them to create proper <pre><code class="language-X"> elements. */
function stripOuterCodeFence(s: string): string {
  const trimmed = s.trim();
  if (trimmed.startsWith('```') && trimmed.endsWith('```')) {
    // Extract the opening fence line to check for a language tag
    const firstLine = trimmed.split('\n')[0];
    const langTag = firstLine.replace(/^```/, '').trim();
    // If there's a language tag, keep the fence so marked renders it as a code block
    if (langTag) return s;
    // Generic fence (no language) — strip it
    const withoutOpening = trimmed.replace(/^```\n/, '');
    return withoutOpening.replace(/\n```$/, '');
  }
  return s;
}

export function MarkdownContent({ text, class: cls, streaming, basePath, ttsMode, activeTtsSegment, onTtsSegmentClick, onSegmentsReady, onDiagramNodeClick }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const { cleanText, presentations } = useMemo(() => extractPresentations(text), [text]);

  // ── TTS mode: annotate block elements + maintain active-segment highlight ─
  useEffect(() => {
    if (!ttsMode || !ref.current) return;
    const container = ref.current;
    const els = Array.from(container.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, pre'));
    const segments: TtsDocSegment[] = [];
    els.forEach((el, idx) => {
      (el as HTMLElement).dataset.ttsSegment = String(idx);
      const isPre = el.tagName === 'PRE';
      // Don't narrate code: standalone <pre> is skipped, and for prose blocks
      // (e.g. an <li> with an embedded fenced code block) strip the nested
      // <pre> text — including its language badge + "Copy" chrome — before
      // reading. Otherwise "Deploy the worker: bash Copy npx wrangler deploy"
      // gets spoken aloud.
      let ttsText = '';
      if (!isPre) {
        if (el.querySelector('pre')) {
          const clone = el.cloneNode(true) as HTMLElement;
          clone.querySelectorAll('pre').forEach((p) => p.remove());
          ttsText = clone.textContent?.trim() ?? '';
        } else {
          ttsText = (el as HTMLElement).textContent?.trim() ?? '';
        }
      }
      const skip = isPre || ttsText.length === 0;
      if (skip) (el as HTMLElement).dataset.ttsSkip = 'true';
      segments.push({ index: idx, ttsText, skip });
    });
    onSegmentsReady?.(segments);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, ttsMode]); // re-annotate when content changes

  // ── TTS mode teardown ───────────────────────────────────────────────────────
  // When ttsMode goes falsy (a read stopped → ChatArea swaps this message back to
  // the non-ttsMode <MarkdownContent>, which Preact reconciles into the SAME
  // instance), the annotate effect above re-runs but bails at `if (!ttsMode)`
  // WITHOUT removing the attrs it added — so the stopped message kept its
  // data-tts-segment markers and the last paragraph kept .tts-active. Strip them
  // here so the message fully exits reading mode (FIX B).
  useEffect(() => {
    if (ttsMode || !ref.current) return;
    const container = ref.current;
    container.querySelectorAll('[data-tts-segment]').forEach((el) => {
      el.classList.remove('tts-active');
      delete (el as HTMLElement).dataset.ttsSegment;
      delete (el as HTMLElement).dataset.ttsSkip;
    });
  }, [ttsMode]);

  const autoCenter = useDocTtsStore((s) => s.autoCenter);

  // Active segment highlight (+ optional auto-center)
  useEffect(() => {
    if (!ttsMode || !ref.current || activeTtsSegment === undefined) return;
    const container = ref.current;
    container.querySelectorAll('[data-tts-segment].tts-active').forEach((el) => {
      el.classList.remove('tts-active');
    });
    const target = container.querySelector(`[data-tts-segment="${activeTtsSegment}"]`);
    if (target) {
      target.classList.add('tts-active');
      // Auto-center is OFF by default (#1564) — only follow the playhead when the
      // operator opted in via the playback bar, so a manual scroll isn't yanked back.
      if (autoCenter) target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [activeTtsSegment, ttsMode, autoCenter]);

  // Manual "center on current paragraph" — view-layer only (R-A2), fired by the
  // playback bar's ⊙ button via a window CustomEvent. Scrolls the active segment to center.
  useEffect(() => {
    if (!ttsMode) return;
    const onCenter = () => {
      const container = ref.current;
      if (!container || activeTtsSegment === undefined) return;
      container
        .querySelector(`[data-tts-segment="${activeTtsSegment}"]`)
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    };
    window.addEventListener('doc-tts-center', onCenter);
    return () => window.removeEventListener('doc-tts-center', onCenter);
  }, [ttsMode, activeTtsSegment]);

  // Mermaid rendering — skip during streaming (diagrams render after streaming ends)
  useEffect(() => {
    if (streaming) return;
    if (!ref.current) return;
    const codeBlocks = ref.current.querySelectorAll('pre > code.language-mermaid');
    if (codeBlocks.length === 0) return;

    getMermaid().then(async (m) => {
      for (const code of Array.from(codeBlocks)) {
        const pre = code.parentElement;
        if (!pre || pre.dataset.mermaidRendered) continue;
        pre.dataset.mermaidRendered = '1';

        const source = (code.textContent || '').trim();
        if (!source) continue;

        const id = `mermaid-${Math.random().toString(36).slice(2, 8)}`;

        try {
          const { svg } = await enqueueMermaidRender(() => m.default.render(id, source));
          // Clean up the render container mermaid left on body
          cleanupMermaidErrors(id);

          const wrapper = document.createElement('div');
          // Arch-map opt-in: when node clicks are handled (the /arch page), the wrapper gets a marker
          // class so CSS re-enables pointer-events on the node anchors (the base rule sets
          // `.mermaid-wrapper svg { pointer-events: none }` for the chat tap-to-zoom behaviour, which
          // would otherwise swallow every node click). See handleClick's geometric fallback too.
          wrapper.className = onDiagramNodeClick ? 'mermaid-wrapper mermaid-wrapper--nodes' : 'mermaid-wrapper';
          wrapper.innerHTML = svg;

          const toggle = document.createElement('button');
          toggle.className = 'mermaid-toggle';
          toggle.textContent = 'Show source';
          toggle.onclick = () => {
            const srcEl = wrapper.querySelector('.mermaid-source') as HTMLElement | null;
            if (srcEl) {
              const visible = srcEl.style.display !== 'none';
              srcEl.style.display = visible ? 'none' : 'block';
              toggle.textContent = visible ? 'Show source' : 'Hide source';
            }
          };
          wrapper.appendChild(toggle);

          const srcEl = document.createElement('pre');
          srcEl.className = 'mermaid-source';
          srcEl.style.display = 'none';
          srcEl.textContent = source;
          wrapper.appendChild(srcEl);

          pre.replaceWith(wrapper);
        } catch (err) {
          // Clean up mermaid's stray render container from the DOM body
          cleanupMermaidErrors(id);

          // Show inline error instead of raw code block
          pre.dataset.mermaidRendered = '1';
          const errWrapper = document.createElement('div');
          errWrapper.className = 'mermaid-error';
          errWrapper.innerHTML = `<span class="mermaid-error-label">Diagram syntax error</span>`;
          const srcEl = document.createElement('pre');
          srcEl.className = 'mermaid-source';
          srcEl.style.display = 'block';
          srcEl.textContent = source;
          errWrapper.appendChild(srcEl);
          pre.replaceWith(errWrapper);
        }
      }
    }).catch((err) => {
      // Mermaid library failed to load — add fallback styling so blocks aren't naked raw code.
      // The hljs effect skips language-mermaid blocks, so without this they get no header/badge/copy.
      const errMsg = err instanceof Error ? err.message : String(err);
      console.error('[mermaid] Library load failed, falling back to code block display:', err);
      for (const code of Array.from(codeBlocks)) {
        const pre = code.parentElement;
        if (!pre || pre.dataset.mermaidRendered) continue;
        pre.dataset.mermaidRendered = '1';
        pre.classList.add('code-block-enhanced');
        const header = document.createElement('div');
        header.className = 'code-block-header';
        const badge = document.createElement('span');
        badge.className = 'code-block-lang';
        badge.textContent = 'mermaid';
        header.appendChild(badge);
        // Show error reason inline so mobile users can see it without DevTools
        const errLabel = document.createElement('span');
        errLabel.className = 'code-block-lang';
        errLabel.style.cssText = 'color:#ef4444;font-size:10px;margin-left:4px;max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap';
        errLabel.textContent = errMsg;
        errLabel.title = errMsg;
        header.appendChild(errLabel);
        const copyBtn = document.createElement('button');
        copyBtn.className = 'code-block-copy';
        copyBtn.textContent = 'Copy';
        copyBtn.onclick = (e) => {
          (e as Event).stopPropagation();
          navigator.clipboard.writeText(code.textContent || '').then(() => {
            copyBtn.textContent = 'Copied!';
            setTimeout(() => { copyBtn.textContent = 'Copy'; }, 1500);
          });
        };
        header.appendChild(copyBtn);
        pre.insertBefore(header, pre.firstChild);
      }
    });
  }, [text, streaming]);

  // Phase 5: Syntax highlighting + copy button + language badge — skip during streaming
  useEffect(() => {
    if (streaming) return;
    if (!ref.current) return;
    const codeBlocks = ref.current.querySelectorAll('pre > code');
    if (codeBlocks.length === 0) return;

    getHljs().then((hljs) => {
      for (const code of Array.from(codeBlocks)) {
        const pre = code.parentElement;
        if (!pre || pre.dataset.hljsProcessed) continue;
        // Skip mermaid blocks (handled by mermaid effect)
        if (code.classList.contains('language-mermaid')) continue;
        pre.dataset.hljsProcessed = '1';

        // Detect language from class (e.g. language-typescript → typescript)
        const langClass = Array.from(code.classList).find((c) => c.startsWith('language-'));
        const lang = langClass ? langClass.replace('language-', '') : null;

        // Apply syntax highlighting
        try {
          if (lang && hljs.getLanguage(lang)) {
            code.innerHTML = hljs.highlight(code.textContent || '', { language: lang }).value;
          } else if (!lang) {
            hljs.highlightElement(code as HTMLElement);
          }
        } catch {
          // Ignore highlight failures — show raw text
        }

        // Wrap in a header bar
        pre.classList.add('code-block-enhanced');

        const header = document.createElement('div');
        header.className = 'code-block-header';

        // Language badge
        const detectedLang = lang || (code as HTMLElement).dataset.hljsLanguage || '';
        if (detectedLang) {
          const badge = document.createElement('span');
          badge.className = 'code-block-lang';
          badge.textContent = detectedLang;
          header.appendChild(badge);
        }

        // Copy button
        const copyBtn = document.createElement('button');
        copyBtn.className = 'code-block-copy';
        copyBtn.textContent = 'Copy';
        copyBtn.onclick = (e) => {
          e.stopPropagation();
          const rawText = code.textContent || '';
          navigator.clipboard.writeText(rawText).then(() => {
            copyBtn.textContent = 'Copied!';
            setTimeout(() => { copyBtn.textContent = 'Copy'; }, 1500);
          });
        };
        header.appendChild(copyBtn);

        pre.insertBefore(header, pre.firstChild);
      }
    });
  }, [text, streaming]);

  // Delegated click handler for file links, images, mermaid diagrams, and slash commands
  const handleClick = useCallback((e: MouseEvent) => {
    // Mermaid diagram click → serialize SVG to data URL → open in lightbox
    const mermaidWrapper = (e.target as HTMLElement).closest('.mermaid-wrapper') as HTMLElement | null;
    if (mermaidWrapper) {
      // Don't intercept clicks on the toggle/source buttons
      if ((e.target as HTMLElement).closest('.mermaid-toggle, .mermaid-source')) return;
      // Arch-map opt-in: a node-select anchor (#arch/...) drills/selects instead of lightboxing.
      // Mermaid emits SVG1 anchors, so the href lives on `xlink:href` (Phase 0 finding), not `href`.
      if (onDiagramNodeClick) {
        e.preventDefault();
        e.stopPropagation();
        // Primary: the click landed inside a node anchor (the --nodes CSS re-enables pointer-events).
        const a = (e.target as HTMLElement).closest('a');
        let href = a?.getAttribute('xlink:href') || a?.getAttribute('href') || null;
        // Fallback: the SVG has `pointer-events:none`, so if the CSS affordance ever fails to apply,
        // e.target is the wrapper and closest('a') is null. Geometrically hit-test the click point
        // against the node anchors so node clicks work regardless of the pointer-events CSS — this is
        // the exact failure mode that shipped /arch un-clickable (DIFFICULTIES-LOG).
        if (!href || !href.startsWith('#arch/')) {
          const svg = mermaidWrapper.querySelector('svg');
          let smallest = Infinity;
          for (const anchor of Array.from(svg?.querySelectorAll('a') || [])) {
            const h = anchor.getAttribute('xlink:href') || anchor.getAttribute('href');
            if (!h || !h.startsWith('#arch/')) continue;
            const r = anchor.getBoundingClientRect();
            if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) {
              const area = r.width * r.height;
              if (area < smallest) { smallest = area; href = h; } // smallest box = most specific node
            }
          }
        }
        if (href && href.startsWith('#arch/')) onDiagramNodeClick(href);
        // Arch mode is a node-interaction surface, not a zoom target — never fall through to the lightbox.
        return;
      }
      const svgEl = mermaidWrapper.querySelector('svg');
      if (svgEl) {
        e.preventDefault();
        e.stopPropagation();
        const serializer = new XMLSerializer();
        const svgStr = serializer.serializeToString(svgEl);
        const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgStr)}`;
        window.dispatchEvent(new CustomEvent('open-image-overlay', { detail: dataUrl }));
      }
      return;
    }
    // Image click → lightbox
    const imgTarget = (e.target as HTMLElement).closest('img') as HTMLImageElement | null;
    if (imgTarget && imgTarget.src) {
      e.preventDefault();
      e.stopPropagation();
      window.dispatchEvent(new CustomEvent('open-image-overlay', { detail: imgTarget.src }));
      return;
    }
    // Plan choice tap → send selection immediately (no popover)
    const planTarget = (e.target as HTMLElement).closest('.plan-choice-btn') as HTMLElement | null;
    if (planTarget) {
      e.preventDefault();
      e.stopPropagation();
      const cmd = planTarget.dataset.command;
      if (cmd) window.dispatchEvent(new CustomEvent('execute-slash-cmd', { detail: { cmd } }));
      return;
    }
    // Slash command tap → show "Here / Elsewhere" bubble
    const slashTarget = (e.target as HTMLElement).closest('.slash-cmd') as HTMLElement | null;
    if (slashTarget) {
      e.preventDefault();
      e.stopPropagation();
      const cmd = slashTarget.dataset.command;
      if (cmd) window.dispatchEvent(new CustomEvent('show-slash-options', {
        detail: { cmd, x: e.clientX, y: e.clientY },
      }));
      return;
    }
    // File link click (linkifyFilePaths-wrapped bare path) → code viewer
    const fileLink = (e.target as HTMLElement).closest('a.file-link') as HTMLAnchorElement | null;
    if (fileLink) {
      e.preventDefault();
      e.stopPropagation();
      const path = fileLink.dataset.path || '';
      const line = fileLink.dataset.line ? parseInt(fileLink.dataset.line, 10) : undefined;
      if (path) {
        window.dispatchEvent(new CustomEvent('open-file-viewer', { detail: { path, line } }));
      }
      return;
    }
    // Generic markdown link — resolve relative/workspace hrefs to the CodeViewer
    // instead of letting the browser navigate (which 404s on workspace paths).
    const anchor = (e.target as HTMLElement).closest('a') as HTMLAnchorElement | null;
    if (anchor) {
      const resolved = resolveMarkdownHref(anchor.getAttribute('href'), basePath);
      if (resolved.type === 'file') {
        e.preventDefault();
        e.stopPropagation();
        window.dispatchEvent(new CustomEvent('open-file-viewer', { detail: { path: resolved.path } }));
      }
      // In TTS mode, link clicks do NOT seek — they still drill down as normal.
      return;
    }
    // TTS mode: non-link click → seek to clicked paragraph
    if (ttsMode && onTtsSegmentClick) {
      let el = e.target as HTMLElement | null;
      while (el && el.dataset.ttsSegment === undefined) el = el.parentElement;
      if (el && el.dataset.ttsSegment !== undefined) {
        e.preventDefault();
        onTtsSegmentClick(parseInt(el.dataset.ttsSegment, 10));
      }
    }
  }, [basePath, ttsMode, onTtsSegmentClick, onDiagramNodeClick]);

  return (
    <>
      <div
        ref={ref}
        class={`${cls || 'md-content'}${ttsMode ? ' tts-reading-doc' : ''}`}
        onClick={handleClick as any}
        dangerouslySetInnerHTML={{ __html: renderMarkdown(stripOuterCodeFence(cleanText), streaming) }}
      />
      {presentations.map((pres, i) => (
        <PresentationCard key={i} title={pres.title} slideCount={pres.slides.length} slides={pres.slides} />
      ))}
    </>
  );
}
